/**
 * Fog Scene
 * Animated landing page background: misty ridgelines, a small hiker walking
 * across the right half of the screen, and fog drifting over everything.
 *
 * Auto-initializes on any <canvas data-fog-scene>. Renders a single still
 * frame when the user prefers reduced motion.
 */

import { COLORS } from './colors';

interface FogPuff {
    x: number;  // horizontal start, fraction of width
    y: number;  // vertical center, fraction of height
    r: number;  // radius, fraction of the larger viewport side
    v: number;  // drift speed, widths per second
    a: number;  // peak opacity
}

const PUFF_COUNT = 14;
const MAX_DPR = 2;

// Hiker walks from 58% of the width to just past the right edge
const HIKER_START = 0.58;
const HIKER_SPAN = 0.5;
const HIKER_SPEED = 0.006;   // widths per second
const HIKER_FADE_IN = 0.06;  // portion of the walk spent emerging from the fog

function createPuffs(): FogPuff[] {
    return Array.from({ length: PUFF_COUNT }, () => ({
        x: Math.random(),
        y: 0.35 + Math.random() * 0.6,
        r: 0.25 + Math.random() * 0.35,
        v: 0.004 + Math.random() * 0.008,
        a: 0.10 + Math.random() * 0.12
    }));
}

/**
 * Start the fog scene on a canvas. Returns a function that stops the animation.
 */
export function initFogScene(canvas: HTMLCanvasElement): () => void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return () => { };

    const palette = COLORS.fogScene;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const puffs = createPuffs();
    let w = 0;
    let h = 0;
    let frameId = 0;

    function resize(): void {
        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        w = canvas.clientWidth;
        h = canvas.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function ridge(y: number, amp: number, seed: number, color: string): void {
        ctx!.fillStyle = color;
        ctx!.beginPath();
        ctx!.moveTo(0, h);
        for (let x = 0; x <= w; x += 8) {
            const t = x / w;
            ctx!.lineTo(x, y + Math.sin(t * 6 + seed) * amp + Math.sin(t * 17 + seed * 2) * amp * 0.3);
        }
        ctx!.lineTo(w, h);
        ctx!.fill();
    }

    function hiker(t: number): void {
        const progress = (t * HIKER_SPEED) % HIKER_SPAN;
        const px = (HIKER_START + progress) * w;
        const py = h * 0.78 - 18;
        const step = Math.sin(t * 5) * 3;

        ctx!.globalAlpha = Math.min(1, progress / HIKER_FADE_IN);
        ctx!.fillStyle = palette.hiker;
        ctx!.fillRect(px - 3, py, 6, 14);                         // body
        ctx!.fillRect(px - 5, py + 1, 4, 9);                      // pack
        ctx!.beginPath();
        ctx!.arc(px, py - 4, 3.2, 0, Math.PI * 2);                // head
        ctx!.fill();
        ctx!.fillRect(px - 2 + step * 0.4, py + 14, 2, 8);        // legs
        ctx!.fillRect(px + 1 - step * 0.4, py + 14, 2, 8);
        ctx!.globalAlpha = 1;
    }

    function fog(t: number): void {
        for (const p of puffs) {
            const x = ((p.x + t * p.v) % 1.6 - 0.3) * w;
            const y = p.y * h;
            const r = p.r * Math.max(w, h);
            const g = ctx!.createRadialGradient(x, y, 0, x, y, r);
            g.addColorStop(0, `rgba(${palette.fogRgb}, ${p.a})`);
            g.addColorStop(1, `rgba(${palette.fogRgb}, 0)`);
            ctx!.fillStyle = g;
            ctx!.fillRect(x - r, y - r, r * 2, r * 2);
        }
    }

    function frame(ms: number): void {
        const t = ms / 1000;
        const sky = ctx!.createLinearGradient(0, 0, 0, h);
        sky.addColorStop(0, palette.skyTop);
        sky.addColorStop(1, palette.skyBottom);
        ctx!.fillStyle = sky;
        ctx!.fillRect(0, 0, w, h);

        ridge(h * 0.52, h * 0.05, 1.3, palette.ridgeFar);
        ridge(h * 0.64, h * 0.06, 4.1, palette.ridgeMid);
        hiker(t);
        ridge(h * 0.80, h * 0.04, 7.7, palette.ridgeNear);
        fog(t);

        if (!still) frameId = requestAnimationFrame(frame);
    }

    function onResize(): void {
        resize();
        if (still) frame(0);
    }

    resize();
    window.addEventListener('resize', onResize);
    frameId = requestAnimationFrame(frame);

    return () => {
        cancelAnimationFrame(frameId);
        window.removeEventListener('resize', onResize);
    };
}

function initAll(): void {
    document.querySelectorAll<HTMLCanvasElement>('canvas[data-fog-scene]').forEach(initFogScene);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}
